import { agentClient } from "../../lib/agentClient";
import { bearer } from "../../lib/accessToken";
import type { EndReportPriority, EndReportRecord } from "../../../../shared/end-report";

export interface EndReportScope {
  token: string;
  orgId: string;
  projectId: string;
  userId: string;
  threadId: string;
  /** The workflow the conversation belongs to: its reports live under its routes. */
  workflowId: string;
}

/** Every round saved in this thread's Workspace, oldest first. */
export async function readEndReports(scope: EndReportScope, signal: AbortSignal): Promise<EndReportRecord[]> {
  const query = new URLSearchParams({
    organization_id: scope.orgId,
    project_id: scope.projectId,
    user_id: scope.userId,
  });
  const path = `/workflows/${encodeURIComponent(scope.workflowId)}/threads/${encodeURIComponent(scope.threadId)}/end-reports`;
  const response = await agentClient.fetch(`${path}?${query}`, {
    signal,
    headers: { Authorization: bearer(scope.token) },
  });
  const body = await response.json();
  if (!response.ok)
    throw new Error(
      typeof body.error === "string"
        ? body.error
        : (body.error?.message ?? body.message ?? `HTTP ${response.status}`),
    );
  return Array.isArray(body.reports) ? body.reports : [];
}

/** The follow-up message: the picked steps by their labels, then the user's own words. */
export function continueMessage(
  labels: readonly string[],
  note: string,
  t: (key: string) => string,
): string {
  const parts: string[] = [];
  if (labels.length) parts.push(`${t("endReport.continueWith")}\n${labels.map((label) => `- ${label}`).join("\n")}`);
  if (note.trim()) parts.push(`${t("workSettings.currentRequest")}\n${note.trim()}`);
  return parts.join("\n\n");
}

const RANK: Record<EndReportPriority, number> = { high: 0, medium: 1, low: 2 };

/** Most important first; equal ones keep the model's order. */
export function byPriority<T extends { priority?: EndReportPriority; recommended?: boolean }>(list: readonly T[]): T[] {
  return list.map((item, i) => [item, i] as const)
    .sort(([a, i], [b, j]) => RANK[a.priority ?? "medium"] - RANK[b.priority ?? "medium"]
      || Number(b.recommended === true) - Number(a.recommended === true) || i - j)
    .map(([item]) => item);
}
